import { tokens } from '../tokens.js';
/**
 * Semantic aliases for the application shell (CP3.1 spec). Feature code names a ROLE, never
 * a Fluent key, so a restyle of the shell is a change here. Raw px values are permitted in
 * packages/ui only (CLAUDE.md rule 2).
 */
export const shellTokens = {
    canvas: tokens.colorNeutralBackground2,
    surface: tokens.colorNeutralBackground1,
    divider: tokens.colorNeutralStroke2,
    controlEdge: tokens.colorNeutralStroke1,
    action: tokens.colorBrandBackground,
    actionHover: tokens.colorBrandBackgroundHover,
    hover: tokens.colorNeutralBackground1Hover,
    /** The current page in the side menu: a soft band, a deeper green name and icon, and a short bar on its left (#742). */
    navActiveBackground: tokens.colorNavActiveBackground,
    navActiveForeground: tokens.colorNavActiveForeground,
    navActiveMarker: tokens.colorNavActiveMarker,
    /** The initials at the menu's foot stay a solid brand circle with white letters, as drawn (#742, N1). */
    avatarBackground: tokens.colorBrandBackground,
    avatarForeground: tokens.colorNeutralForegroundOnBrand,
    focusRing: tokens.colorStrokeFocus2,
    text1: tokens.colorNeutralForeground1,
    text2: tokens.colorNeutralForeground2,
    text3: tokens.colorNeutralForeground3,
    /** A destination that is switched off. Never the only sign of it: the row also says why in words. */
    textDisabled: tokens.colorNeutralForegroundDisabled,
    /** A menu row's count: a colour pair that reads at 4.5:1 in both themes (#743's review). */
    badgeBackground: tokens.colorNavBadgeBackground,
    badgeForeground: tokens.colorNavBadgeForeground,
    scrim: 'rgba(0, 0, 0, 0.4)',
};
/**
 * The record rail's two frame numbers (Figma 294:667), kept as numbers so the side padding is
 * DERIVED rather than guessed. The frame draws a 220px column holding 194px of content, which is
 * only true at 13px of side padding — no spacing token is 13px, and picking the nearest one left
 * the content 6px narrow, which is what made `Documents & checklist` wrap off-frame (#337).
 */
const recordRailWidthPx = 220;
const recordRailContentPx = 194;
/** Shell geometry. Breakpoints are the approved tiers; targets meet the 44px rule. */
export const shellMetrics = {
    sidebarWidth: '248px',
    railWidth: '56px',
    drawerWidth: '300px',
    headerHeight: '48px',
    mobileHeaderHeight: '56px',
    touchTarget: '44px',
    navRowHeight: '40px',
    recordRailWidth: `${recordRailWidthPx}px`,
    /** What the frame's column must hold; `recordRailPaddingInline` is what makes it hold it. */
    recordRailContent: `${recordRailContentPx}px`,
    recordRailPaddingInline: `${(recordRailWidthPx - recordRailContentPx) / 2}px`,
    /**
     * The rail's unnumbered record links are drawn SHORTER than the numbered steps above them —
     * the frame's way of saying they are not stages of hiring.
     *
     * Below `touchTarget` on purpose. That is legitimate only because the rail itself is hidden
     * below the desktop tier, so no touch-width probe can ever measure one of these links; the 44px
     * floor still governs every tier where a thumb reaches them. That coupling is load-bearing, so
     * it is pinned in `tests/section-workspace-rail.test.tsx` rather than left as a comment —
     * widening the rail's reach to a touch tier fails there until the floor is restored.
     */
    recordRailLinkHeight: '36px',
    /** Reference control geometry (Figma 1:567, 258:4840, 213:4895). */
    controlHeight: '40px',
    fieldHeight: '36px',
    keyValueRow: '48px',
    /** Reference row rhythm for the People list (Figma 136:3937): header 42, body rows 52. */
    tableHeaderRow: '42px',
    tableRow: '52px',
    /**
     * People list column model (Fluent tables are `table-layout: fixed`, so every column must
     * be sized on purpose or they all share the width equally). Identity takes the largest
     * share; the tag and verdict columns are wide enough for their longest label; role and the
     * next-step sentence share what remains and wrap to two lines.
     */
    listIdentityColumn: '26%',
    listIdentityColumnCompact: '30%',
    listRoleColumn: '16%',
    listTagColumn: '7.5rem',
    listVerdictColumn: '8.5rem',
    wideMin: 1440,
    desktopMin: 1280,
    tabletMin: 768,
    /**
     * The width of the page's CONTENT (not the window: the menu and the page padding come off it) from which the Roster's staff
     * table has an Email column (#798). Email is the FIRST column to go on a narrower page: Languages and its heading filter stay at
     * every width the table is drawn (Julio, #798), and an email is still in Edit and on the profile. With the Compliance column
     * drawn (the test site) the table is 11rem fuller, so Email needs 1240 to leave Name room for the name and the job title under it;
     * where Compliance is not drawn (production, staging) 1100 does. Measured in a browser (the page pads 24 a side at 1280 and over
     * and 16 under it): 1144 at 1440 with the menu open, 1176 at 1280 with the menu as icons, 984 at 1280 with the menu widened, and
     * 936 at 1024 with the menu as icons. Under it Phone keeps its own column on a desktop window, and leaves the table below the
     * desktop tier.
     */
    tableEmailMin: 1240,
    tableEmailMinNoCompliance: 1100,
    /**
     * The width of the page's CONTENT from which the roster side of the Roster's Map view is its full width (#689, Figma M1: 400).
     * Under it the side is narrower (Figma M4b: 360), so the map keeps the larger share. Measured in a browser: the content is 984
     * at 1280 with the menu widened, the one drawn case under it.
     */
    mapViewSideWideMin: 1040,
    /**
     * The width of the page's CONTENT under which the roster side of the Map view is its slimmest (#699). The side stays beside the map
     * at every width but the phone, so a tablet's content (680 at 768 with the menu as icons) must still leave the map room: the side
     * is 320 there, 360 from here and 400 from `mapViewSideWideMin`. Measured in a browser: the content is 680 at 768 and 1012 at 1100.
     */
    mapViewSideSlimMin: 800,
};
/**
 * Media queries for the approved tiers, so feature code never spells a pixel width.
 * `belowTablet` is where two-column layouts stack; `phone` is where 44px targets apply.
 */
export const shellMedia = {
    belowTablet: `@media (max-width: ${shellMetrics.desktopMin - 1}px)`,
    phone: `@media (max-width: ${shellMetrics.tabletMin - 1}px)`,
};
/**
 * Pop-up geometry (Figma `1038:2` cards 11, 12, 15 and round 2's card 18; #501, #502). Hand-written here, not in the
 * generated token file: Figma Variables carry no pop-up sizes yet.
 *
 * Two phone numbers with two jobs, which is why there are two. A dialog STACKS (full width with a margin, buttons
 * one above the other, the action on top) under `stackBelowPx`; the 44px touch size applies up to `shellMedia.phone`
 * (767), the app's phone width, so a 700-wide screen keeps its normal dialog width and side-by-side buttons but
 * every button and field is a touch target. The sizes are 480, 560 and up to 960 at the browser's 16px root.
 */
export const popupMetrics = {
    small: '30rem',
    medium: '35rem',
    large: '60rem',
    /** A dialog is never taller than this share of the window; the middle scrolls. */
    maxHeight: '90vh',
    stackBelowPx: 600,
};
export const popupMedia = {
    /** Under `popupMetrics.stackBelowPx`. */
    stack: `@media (max-width: ${popupMetrics.stackBelowPx - 1}px)`,
};
export function tierForWidth(width) {
    if (width >= shellMetrics.wideMin)
        return 'wide';
    if (width >= shellMetrics.desktopMin)
        return 'desktop';
    if (width >= shellMetrics.tabletMin)
        return 'tablet';
    return 'mobile';
}
/** Active route = URL-prefix match, so /app/people/:id keeps People active; /app is exact. */
export function isActiveRoute(currentPath, to, root = '/app') {
    if (to === root)
        return currentPath === root || currentPath === `${root}/`;
    return currentPath === to || currentPath.startsWith(`${to}/`);
}
export const SHELL_EXPANDED_KEY = 'vigeo-hr.shell.expanded';
//# sourceMappingURL=tokens.js.map